import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLoginService } from "../service/loginServices";
import bgImage from "../../../assets/bg.png";
import { motion, AnimatePresence } from "framer-motion";

export default function LoginPage() {
  const [userInput, setUserInput] = useState(""); 
  const [password, setPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const { login, isLoading } = useLoginService();

  const handleSubmit = (e) => {
    e.preventDefault();
    login(
      { email: userInput, password }, 
      {
        onSuccess: () => {
          setShowSuccess(true);
          setTimeout(() => navigate("/"), 2000);
        },
      }
    );
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center px-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <AnimatePresence>
        {showSuccess ? (
          <motion.div
            key="success"
            className="fixed left-4 top-1/2 -translate-y-1/2 z-50 w-full max-w-xs bg-green-600/95 backdrop-blur-lg p-4 rounded-lg text-white shadow-xl text-center flex flex-col items-center border border-green-300"
            initial={{ x: "-120vw", opacity: 0, scale: 0.85 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: "-120vw", opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <svg
              className="mx-auto mb-2"
              width="36"
              height="36"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r="12"
                fill="#22c55e"
                opacity="0.2"
              />
              <path
                d="M8 12.5l2.5 2.5 5-5"
                stroke="#22c55e"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            <h2 className="text-lg font-bold text-white mb-1">
              Login Successful!
            </h2>
            <p className="text-xs text-white/80 mb-1">
              Welcome back! Redirecting to your profile...
            </p>
            <span className="text-xs text-white/60">
              You will be redirected shortly.
            </span>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            className="bg-black/70 backdrop-blur-md p-8 rounded-xl w-full max-w-md text-white shadow-2xl border border-white/10"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-green-400 text-center">
              Login to FitTrack
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block mb-1 text-sm">Email or Username</label>
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Enter your email or username"
                  className="peer w-full px-4 py-2 bg-transparent border-b-2 border-green-400 text-white focus:outline-none focus:border-green-300 transition placeholder-transparent"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 text-sm">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="peer w-full px-4 py-2 bg-transparent border-b-2 border-green-400 text-white focus:outline-none focus:border-green-300 transition placeholder-transparent"
                  required
                />
              </div>

              <motion.button
                type="submit"
                className="w-full bg-green-500 hover:bg-green-400 text-black font-semibold py-2.5 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                disabled={isLoading}
              >
                {isLoading ? "Logging in..." : "Login"}
              </motion.button>
            </form>

            <p className="mt-4 text-sm text-gray-300 text-center">
              Don’t have an account?{" "}
              <Link to="/signup" className="text-green-400 hover:underline">
                Sign up
              </Link>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
